import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { MaterialSymbol } from '@/components/ui/material-symbol';
import { STORE_CATEGORIES, STORE_TAGS } from './storeFilterConstants';

type PlatformKey = 'all' | 'windows' | 'playsolana' | 'web' | 'mobile';

const PLATFORMS: { key: PlatformKey; label: string }[] = [
  { key: 'windows', label: 'Windows' },
  { key: 'web', label: 'Web' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'playsolana', label: 'Desktop client' },
];

function updateParams(
  searchParams: URLSearchParams,
  updates: Record<string, string | null | undefined>
) {
  const params = new URLSearchParams(searchParams.toString());
  Object.entries(updates).forEach(([key, value]) => {
    if (value && value !== '') params.set(key, value);
    else params.delete(key);
  });
  return params;
}

export function StoreFilterSidebar() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const category = searchParams.get('category') || '';
  const tag = searchParams.get('tag') || '';
  const freeParam = searchParams.get('free');
  const freeToPlay = freeParam === 'true' ? true : freeParam === 'false' ? false : null;
  const platform = (searchParams.get('platform') || 'all') as PlatformKey;

  const go = (updates: Record<string, string | null | undefined>) => {
    const params = updateParams(searchParams, updates);
    navigate(`/store?${params.toString()}`);
  };

  const catalogValues = STORE_CATEGORIES as unknown as string[];
  const catalogSelectValue =
    category && catalogValues.includes(category) ? category : 'All Categories';

  return (
    <aside
      className="arc-aside-surface fixed left-0 top-16 z-30 hidden h-[calc(100vh-4rem)] w-72 flex-col overflow-y-auto px-6 py-8 md:flex"
      aria-label="Store filters"
    >
      <div>
        <h3 className="mb-6 px-2 font-display text-lg font-medium text-white">Library Filters</h3>

        <div className="mb-8">
          <span className="mb-4 block px-2 font-display text-[10px] font-semibold uppercase tracking-widest text-slate-500">
            Category
          </span>
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => go({ category: null })}
              className={cn(
                'flex w-full cursor-pointer items-center gap-3 rounded-lg px-4 py-3 transition-all',
                !category
                  ? 'rounded-r-lg border-l-2 border-nav-active bg-blue-500/10 text-nav-active'
                  : 'border-l-2 border-transparent text-slate-500 hover:bg-white/5 hover:text-slate-300'
              )}
            >
              <MaterialSymbol name="grid_view" className="!text-[20px]" />
              All games
            </button>
            <button
              type="button"
              onClick={() => go({ category: 'RPG' })}
              className={cn(
                'flex w-full cursor-pointer items-center gap-3 rounded-lg px-4 py-3 transition-all',
                category === 'RPG'
                  ? 'rounded-r-lg border-l-2 border-nav-active bg-blue-500/10 text-nav-active'
                  : 'border-l-2 border-transparent text-slate-500 hover:bg-white/5 hover:text-slate-300'
              )}
            >
              <MaterialSymbol name="rocket_launch" className="!text-[20px]" />
              New releases
            </button>
            <button
              type="button"
              onClick={() => go({ category: 'Action' })}
              className={cn(
                'flex w-full cursor-pointer items-center gap-3 rounded-lg px-4 py-3 transition-all',
                category === 'Action'
                  ? 'rounded-r-lg border-l-2 border-nav-active bg-blue-500/10 text-nav-active'
                  : 'border-l-2 border-transparent text-slate-500 hover:bg-white/5 hover:text-slate-300'
              )}
            >
              <MaterialSymbol name="trending_up" className="!text-[20px]" />
              Top sellers
            </button>
          </div>
        </div>

        <div className="mb-8">
          <span className="mb-4 block px-2 font-display text-[10px] font-semibold uppercase tracking-widest text-slate-500">
            Genre tags
          </span>
          <div className="flex flex-wrap gap-2 px-2">
            {STORE_TAGS.filter((t) => t !== 'All Tags').map((t) => {
              const active = tag === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => go({ tag: active ? null : t })}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-sm transition-all',
                    active
                      ? 'border-nav-active/50 bg-blue-500/10 text-nav-active'
                      : 'border-white/5 bg-surface-container-high text-slate-500 hover:border-nav-active/40 hover:text-nav-active'
                  )}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-8 px-2">
          <label className="group flex cursor-pointer items-center justify-between">
            <span className="text-slate-500 transition-colors group-hover:text-slate-300">Free-to-play</span>
            <button
              type="button"
              role="switch"
              aria-checked={freeToPlay === true}
              onClick={() => {
                const next =
                  freeToPlay === null ? true : freeToPlay === true ? false : null;
                go({
                  free: next === null ? null : next ? 'true' : 'false',
                });
              }}
              className={cn(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                freeToPlay === true ? 'bg-primary-container' : 'bg-surface-container-highest'
              )}
            >
              <span
                className={cn(
                  'inline-block h-5 w-5 transform rounded-full border border-white/20 bg-white shadow transition-transform',
                  freeToPlay === true ? 'translate-x-5' : 'translate-x-0.5'
                )}
              />
            </button>
          </label>
        </div>

        <div className="mb-8 px-2">
          <span className="mb-4 block font-display text-[10px] font-semibold uppercase tracking-widest text-slate-500">
            Platform
          </span>
          <div className="space-y-3">
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="radio"
                name="store-platform"
                checked={platform === 'all'}
                onChange={() => go({ platform: null })}
                className="h-4 w-4 rounded border-white/10 bg-surface-container text-nav-active focus:ring-nav-active/30"
              />
              <span className="text-sm text-slate-500">All platforms</span>
            </label>
            {PLATFORMS.map((p) => (
              <label key={p.key} className="flex cursor-pointer items-center gap-3">
                <input
                  type="radio"
                  name="store-platform"
                  checked={platform === p.key}
                  onChange={() => go({ platform: p.key })}
                  className="h-4 w-4 rounded border-white/10 bg-surface-container text-nav-active focus:ring-nav-active/30"
                />
                <span className="text-sm text-slate-500">{p.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mb-8 px-2">
          <span className="mb-2 block font-display text-[10px] font-semibold uppercase tracking-widest text-arc-text-meta">
            Catalog
          </span>
          <select
            value={catalogSelectValue}
            onChange={(e) => {
              const v = e.target.value;
              go({ category: v === 'All Categories' ? null : v });
            }}
            className="w-full rounded-lg border border-white/10 bg-surface-container-lowest/50 px-3 py-2 text-sm text-white focus:border-nav-active/50 focus:outline-none focus:ring-2 focus:ring-nav-active/20"
          >
            {STORE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-auto px-2">
        <Link
          to="/store/submit"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-400 py-4 font-display text-sm font-bold text-on-primary shadow-lg shadow-blue-950/30 transition-transform active:scale-95"
        >
          <MaterialSymbol name="add_circle" className="!text-xl" />
          Submit game
        </Link>
      </div>
    </aside>
  );
}
